import { Component, Input, OnInit } from '@angular/core';
import { IndicationDefinition, IndicationGroup } from '@app/models/indication';
import { SelectionsModel, ControlTypeEnum } from '@app/models/selections-model';
import { SharedService } from '@app/services/shared.service';
import { VennHealthHelpers } from '@app/shared/vennHealthHelpers';
import { CheckableSettings, CheckedState } from '@progress/kendo-angular-treeview';
import { asapScheduler, first, Observable, of, skip } from 'rxjs';

@Component({
  selector: 'app-checkbox-treeview',
  templateUrl: './checkbox-treeview.component.html',
  styleUrls: ['./checkbox-treeview.component.css']
})
export class CheckboxTreeviewComponent implements OnInit {

  @Input() indication: IndicationDefinition;
  @Input() labelName: string = 'Select criteria to include:';
  @Input() hasCustomControlParent: boolean = false;

  checkedKey = 'displayName';

  public get checkableSettings(): CheckableSettings {
    return {
      checkChildren: true,
      checkDisabledChildren: false,
      checkParents: true,
      enabled: true,
      mode: 'multiple',
      checkOnClick: true,
    };
  }

  public children = (dataItem: any): Observable<any[]> => of(dataItem.items);
  public hasChildren = (dataItem: any): boolean => !!dataItem.items;
  public checkedKeys: any[] = [];
  hasErrors = false;
  
  allIndications: IndicationGroup[] = [];

  constructor(private sharedService: SharedService) { }

  ngOnInit() {
    // only called first time to setup form and update selected filters via shared service
    this.sharedService.allIndications.pipe(first()).subscribe(data => {
      if (data) {
        this.allIndications = data;
        this.setIndicationSelections();
      }
    });
    // called everytime after the first time
    this.sharedService.allIndications.pipe(skip(1)).subscribe(data => {
      if (data) {
        this.allIndications = data;
        this.checkForSelectAll();
      }
    });

    this.sharedService.activeSavedFilters.subscribe(data => {
      if (data) {
        const loadedIndication = VennHealthHelpers.getCurrentIndicationFromIndicationGroupJson(this.indication, this.allIndications);
        this.indication.selections = loadedIndication.selections;
        this.indication.selected = loadedIndication.selected;

        this.setIndicationSelections();
      }
    });
  }

  setIndicationSelections() {
    if (this.indication.selected && this.indication.selections.length > 0) {
      asapScheduler.schedule(() => {
        this.checkedKeys = this.indication.selections[0].selectionValue;
        this.updateSelections();
      });
    } else {
      this.setDefaults();
    }
  }

  setDefaults() {
    asapScheduler.schedule(() => {
      this.checkedKeys = [this.indication.inputOptions[0].displayName].concat(this.indication.inputOptions[0]['items'].map(i => i.displayName));
      this.updateSelections();
    });
  }

  checkedChange(event: any) {
    this.hasErrors = this.checkedKeys.length === 0;
    this.updateSelections();
  }

  checkForSelectAll(): void {
    if (this.checkedKeys.length === this.indication.inputOptions[0]['items'].length) {
      this.checkedKeys.unshift('Select All');
    }
  }

  updateSelections(): void {
      // only show the selected options to avoid showing the "Select All" option
      let selections = [];
      this.indication.inputOptions[0]['items'].forEach(i => {
        if(this.checkedKeys.indexOf(i.displayName) >= 0) {
          selections.push(i);
        };
      });
      selections = selections.sort((a,b) => a.order - b.order).map(i => i.displayName);

      const currentSelection: SelectionsModel = {
        indicationName: this.indication.name,
        controlName: 'selections',
        controlType: ControlTypeEnum.CHECKBOX,
        controlDisplayName: 'Selections',
        order: 1,
        selectionValue: selections,
        selectionDisplayName: 'Selections'
      };
      this.indication.selections = [currentSelection];
      const currentIndicationGroup = VennHealthHelpers.getIndicationGroup(this.indication);
      const matchingGroupIndex = this.allIndications.findIndex(i => i.name === currentIndicationGroup.name);
      const matchingIndicationIndex = this.allIndications[matchingGroupIndex].indications.findIndex(i => i.name === this.indication.name);
      this.allIndications[matchingGroupIndex].indications[matchingIndicationIndex] = this.indication;
      this.sharedService.setAllIndications(this.allIndications);
  }

  // Custom logic handling Indeterminate state when custom data item property is persisted
  public isChecked = (dataItem: any, index: string): CheckedState => {
    if (this.containsItem(dataItem)) {
      return "checked";
    }

    // if (this.isIndeterminate(dataItem.items)) {
    //   return "indeterminate";
    // }

    return "none";
  };

  private containsItem(item: any): boolean {
    return this.checkedKeys.indexOf(item[this.checkedKey]) > -1;
  }

  // private isIndeterminate(items: any[] = []): boolean {
  //   let idx = 0;
  //   let item;

  //   while ((item = items[idx])) {
  //     if (this.isIndeterminate(item.items) || this.containsItem(item)) {
  //       return true;
  //     }

  //     idx += 1;
  //   }

  //   return false;
  // }

}
